import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Alert, Divider, Form, Modal, Space, Switch, Typography } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import NumberField from "../../../components/common/fields/NumberField";
import {
  CancellationPolicy,
  CancellationPolicyRequest,
  CancellationPolicyTier,
} from "../../../@type/billing.types";

interface Props {
  open: boolean;
  policy: CancellationPolicy | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (request: CancellationPolicyRequest) => void;
}

const DEFAULT_POLICY: CancellationPolicyRequest = {
  is_active: true,
  no_show_fee_percent: 100,
  refund_to_credit_allowed: false,
  platform_fee_share_percent: 50,
  owner_cancel_min_hours: 3,
  tiers: [
    { min_hours_before_start: 24, fee_percent: 0 },
    { min_hours_before_start: 12, fee_percent: 20 },
    { min_hours_before_start: 3, fee_percent: 50 },
    { min_hours_before_start: 0, fee_percent: 100 },
  ],
};

/** Same rules as the server: a 0h tier, unique hours, fee never drops closer to the start. */
export type TierErrorCode = "required" | "duplicate" | "zero" | "decreasing";

export const validateTiers = (tiers: CancellationPolicyTier[]): TierErrorCode | null => {
  if (!tiers.length) return "required";
  const hours = tiers.map((tier) => tier.min_hours_before_start);
  if (new Set(hours).size !== hours.length) return "duplicate";
  if (!hours.includes(0)) return "zero";
  const sorted = [...tiers].sort((a, b) => b.min_hours_before_start - a.min_hours_before_start);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].fee_percent < sorted[i - 1].fee_percent) {
      return "decreasing";
    }
  }
  return null;
};

/** Mirrors CancellationPolicyService.calculate for a quick what-if. */
export const previewFee = (
  tiers: CancellationPolicyTier[],
  hoursBefore: number,
  paidAmount: number,
  tax: number
) => {
  const sorted = [...tiers].sort((a, b) => b.min_hours_before_start - a.min_hours_before_start);
  const tier =
    sorted.find((item) => item.min_hours_before_start <= hoursBefore) ?? sorted[sorted.length - 1];
  const feePercent = tier ? tier.fee_percent : 0;
  const feeBase = Math.max(paidAmount - tax, 0);
  const fee = Math.min(Math.round(((feeBase * feePercent) / 100) * 100) / 100, paidAmount);
  return { feePercent, fee, refund: paidAmount - fee };
};

const CancellationPolicyEditor = ({ open, policy, saving, onCancel, onSave }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CancellationPolicyRequest>();
  const [previewHours, setPreviewHours] = useState(6);
  const [previewPaid, setPreviewPaid] = useState(100);
  const [previewTax, setPreviewTax] = useState(0);
  const tiers = Form.useWatch("tiers", form) ?? [];

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(policy ? { ...policy } : DEFAULT_POLICY);
  }, [open, policy, form]);

  const tierError = useMemo(() => validateTiers(tiers), [tiers]);
  const preview = useMemo(
    () => previewFee(tiers, previewHours, previewPaid, previewTax),
    [tiers, previewHours, previewPaid, previewTax]
  );

  const handleOk = async () => {
    const values = await form.validateFields();
    if (validateTiers(values.tiers)) return;
    onSave(values);
  };

  return (
    <Modal
      title={t("billing.policy.editor_title", { name: policy ? policy.country_name : t("billing.policy.new") })}
      open={open}
      onCancel={onCancel}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={onCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton onClick={handleOk} loading={saving} disabled={Boolean(tierError)}>
            {t("billing.policy.save")}
          </ActionButton>
        </Box>
      }
      width={640}
    >
      <Form form={form} layout="vertical" initialValues={DEFAULT_POLICY}>
        <Space size="large" wrap>
          <Form.Item name="is_active" label={t("billing.policy.active")} valuePropName="checked">
            <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
          </Form.Item>
          <Form.Item name="refund_to_credit_allowed" label={t("billing.policy.refund_to_credit")} valuePropName="checked">
            <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
          </Form.Item>
        </Space>
        <Space size="large" wrap>
          <Form.Item name="no_show_fee_percent" label={t("billing.policy.no_show_fee")} rules={[{ required: true }]}>
            <NumberField min={0} max={100} precision={2} suffix="%" width={160} />
          </Form.Item>
          <Form.Item name="platform_fee_share_percent" label={t("billing.policy.platform_share")} rules={[{ required: true }]}>
            <NumberField min={0} max={100} precision={2} suffix="%" width={160} />
          </Form.Item>
          <Form.Item name="owner_cancel_min_hours" label={t("billing.policy.owner_cancel_hours")} rules={[{ required: true }]}>
            <NumberField min={0} precision={0} suffix="h" width={160} />
          </Form.Item>
        </Space>

        <Divider orientation="left">{t("billing.policy.tiers_divider")}</Divider>
        <Form.List name="tiers">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Space key={field.key} align="baseline" style={{ display: "flex" }}>
                  <Form.Item name={[field.name, "min_hours_before_start"]} rules={[{ required: true }]}>
                    <NumberField min={0} precision={0} prefix={t("billing.policy.from")} suffix="h" width={170} />
                  </Form.Item>
                  <Form.Item name={[field.name, "fee_percent"]} rules={[{ required: true }]}>
                    <NumberField min={0} max={100} precision={2} prefix={t("billing.policy.fee")} suffix="%" width={170} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              ))}
              <CancelButton startIcon={<PlusOutlined />} onClick={() => add({ min_hours_before_start: 0, fee_percent: 100 })}>
                {t("billing.policy.add_tier")}
              </CancelButton>
            </>
          )}
        </Form.List>
        {tierError && <Alert style={{ marginTop: 12 }} type="error" showIcon message={t(`billing.policy.err.${tierError}`)} />}
      </Form>

      <Divider orientation="left">{t("billing.policy.preview")}</Divider>
      <Space wrap>
        <NumberField value={previewHours} min={-24} onChange={(v) => setPreviewHours(v ?? 0)} prefix={t("billing.policy.cancel_prefix")} suffix={t("billing.policy.hours_before")} width={200} />
        <NumberField value={previewPaid} min={0} onChange={(v) => setPreviewPaid(v ?? 0)} prefix={t("billing.policy.paid")} width={150} />
        <NumberField value={previewTax} min={0} onChange={(v) => setPreviewTax(v ?? 0)} prefix={t("billing.policy.tax")} width={150} />
      </Space>
      <Typography.Paragraph style={{ marginTop: 8 }}>
        <Trans
          i18nKey="billing.policy.preview_result"
          values={{ percent: preview.feePercent, fee: preview.fee, refund: preview.refund }}
          components={{ b: <b /> }}
        />
      </Typography.Paragraph>
    </Modal>
  );
};

export default CancellationPolicyEditor;
